import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { AuthLeftContent } from "../../../components/layouts/AuthLeftContent";
import { CustomInput } from "../../../components/common/Input/CustomInput";
import { colorVariables, customValue } from "../../../utils/colorVariable";
import { useNavigate } from "react-router-dom";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { EROUTER } from "../../../utils/enumVariable";
import { signIn } from "../../../services/authService";
import { toast } from "react-toastify";
import { ISignIn } from "../model/auth";
import { useTheme, useMediaQuery } from "@mui/material";
import { useAuth } from "../../user/services/AuthProvider";
import { useDispatch } from "react-redux";
import { setUser } from "../../../redux/auth/authSlice";
import { useQueryClient } from "@tanstack/react-query";
import { setUserData } from "../../../redux/auth/userSlice";
import dayjs from "dayjs";

const INITIAL_STATE: ISignIn = {
  login: "",
  password: "",
} as const;

export const SigninPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { authenticate } = useAuth();
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  // State
  const [signInState, setSignInState] = useState<ISignIn>(INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(false);

  // Media Queries
  const breakpoints = {
    isMobile: useMediaQuery(theme.breakpoints.down("sm")),
    isLarge: useMediaQuery(theme.breakpoints.up("lg")),
    isExtraLarge: useMediaQuery(theme.breakpoints.up("xl")),
  };

  // Handlers
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setSignInState((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleNavigate = useCallback(() => {
    const redirect = new URLSearchParams(window.location.search).get("redirect");
    const redirectPath = sessionStorage.getItem("redirectAfterLogin");
    const targetPath = redirect ? decodeURIComponent(redirect) : redirectPath;
    const isAuthRoute =
      targetPath?.includes(EROUTER.SIGNIN) ||
      targetPath?.includes(EROUTER.SIGNUP) ||
      targetPath?.includes(EROUTER.ACTIVATE_ACCOUNT);

    if (targetPath && !isAuthRoute) {
      // Clear the saved path
      sessionStorage.removeItem("redirectAfterLogin");
      queryClient.clear();
      // Redirect to the saved path
      navigate(targetPath);
    } else {
      sessionStorage.removeItem("redirectAfterLogin");
      queryClient.clear();
      navigate(EROUTER.HOME);
    }
  }, [navigate, queryClient]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // Basic validation
      if (!signInState.login || !signInState.password) {
        toast.error("Please fill in all fields");
        return;
      }

      setIsLoading(true);
      try {
        const response = await signIn(signInState.login, signInState.password);
        console.log("Sign-in response:", response);
        if (response) {
          dispatch(setUser(response.user));
          dispatch(
            setUserData({
              user: response.user,
              profile: response.user_profile,
              access_token: response.access_token,
              refresh_token: response.refresh_token,
            })
          );
          const userData = {
            id_user: response.user?.id_user,
            firstName: response.user?.firstName || "",
            lastName: response.user?.lastName || "",
            email: response.user?.email || "",
            matricule: response.user?.matricule,
            isVerified: response.user?.isVerified || false,
            avatar:response.user?.avatar || "",
            role:
              response.user_profile?.userRole?.role.role_description ||
              undefined,
            department:
              response.user_profile?.userDepartment?.department.description ||
              undefined,
          };
          const functionnalities =
            response.user_profile?.all_functionalities &&
            response.user_profile?.all_functionalities.map(
              (fun) => fun.functionality.functionality
            );
          const factorySection = response.user_profile.factorySection || null;
          toast.success(`Welcome back ${userData.firstName || ""}!`);
          authenticate(userData, functionnalities || [""], factorySection);
          handleNavigate();
        } else {
          throw new Error("Authentication failed");
        }
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Invalid credentials. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      signInState.login,
      signInState.password,
      dispatch,
      authenticate,
      handleNavigate,
    ]
  );

  return (
    <Box
      component={Paper}
      height={{ sm: "80vh", xs: "100vh" }}
      width={breakpoints.isExtraLarge ? "50vw" : "inherit"}
      mx={breakpoints.isExtraLarge || breakpoints.isMobile ? "auto" : 20}
      my={{ sm: 10 }}
      borderRadius={customValue.borderRadius}
      elevation={5}
      display={"flex"}
      flexDirection={"row"}
    >
      {breakpoints.isLarge && <AuthLeftContent />}
      <Box
        position={"relative"}
        width={breakpoints.isLarge ? "50%" : "100%"}
        display={"flex"}
        alignItems={"flex-start"}
        justifyContent={"center"}
        flexDirection={"column"}
        px={10}
        gap={5}
        component={"form"}
        onSubmit={handleSubmit}
      >
        {isLoading && (
          <Box
            position={"absolute"}
            top={0}
            left={0}
            width={"100%"}
            height={"100%"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            flexDirection={"column"}
            gap={2}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              backdropFilter: "blur(3px)",
              borderRadius: customValue.borderRadius,
              zIndex: 10,
            }}
          >
            <CircularProgress size={80} thickness={3} sx={{ color: colorVariables.primary }} />
            <Typography variant="body2" color={colorVariables.primary} fontWeight={500}>
              Signing in…
            </Typography>
          </Box>
        )}

        <Box
          display={{ sm: "none", xs: "flex" }}
          width={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <img
            src={`${process.env.PUBLIC_URL}/accord_knits.png`}
            alt="Logo"
            style={{ height: 100 }}
          />
        </Box>

        <Typography
          variant="h2"
          color={colorVariables.primary}
          fontWeight={"lighter"}
        >
          Sign in
        </Typography>
        <CustomInput
          label="Login"
          name="login"
          placeholder="Enter your login"
          value={signInState.login}
          onChange={handleInputChange}
          type="text"
        />
        <CustomInput
          label="Password"
          name="password"
          placeholder="Enter your password"
          type="password"
          value={signInState.password}
          onChange={handleInputChange}
        />
        {/*<FormControlLabel*/}
        {/*  control={*/}
        {/*    <Checkbox*/}
        {/*      checked={rememberMe}*/}
        {/*      onChange={handleRememberMeChange}*/}
        {/*      color="primary"*/}
        {/*    />*/}
        {/*  }*/}
        {/*  label="Remember me"*/}
        {/*/>*/}
        <Box
          display={"flex"}
          width={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
          gap={2}
        >
          <Button
            fullWidth
            disabled={isLoading}
            sx={{
              background: colorVariables.gradient_primary,
              color: colorVariables.white,
            }}
            type="submit"
          >
            Sign in
          </Button>
          {/*<Typography variant="caption">*/}
          {/*  Don't have an account ?{" "}*/}
          {/*  <span*/}
          {/*    style={{*/}
          {/*      fontWeight: "bolder",*/}
          {/*      cursor: "pointer",*/}
          {/*      color: colorVariables.primary,*/}
          {/*    }}*/}
          {/*    onClick={() => navigate(LINKS.SIGNUP)}*/}
          {/*  >*/}
          {/*    Create an account*/}
          {/*  </span>*/}
          {/*</Typography>*/}
          <Typography variant="caption">
            © {dayjs(new Date()).format("YYYY")} Accord Knits SA, Antananarivo,
            Madagascar
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
